var RevistaSearch = RevistaSearch || {};

// ============================================================================
// GERENCIADOR DE PARÂMETROS
// ============================================================================

RevistaSearch.ParamsManager = {
  buildFromForm() {
    const { FormManager, Validators, Mappers, CONFIG } = RevistaSearch;
    const formData = FormManager.getFormData();

    const validationMessage = Validators.validateDates(
      formData.dateFrom,
      formData.dateTo
    );
    if (validationMessage) {
      return {
        error: {
          type: CONFIG.ERROR_TYPE.VALIDATION,
          message: validationMessage,
        },
      };
    }

    return {
      params: {
        query: formData.query,
        page: CONFIG.PAGINATION.DEFAULT_PAGE,
        size: CONFIG.PAGINATION.DEFAULT_SIZE,
        order: Mappers.sortToOrder(formData.sort),
        searchType: Mappers.matchToSearchType(formData.match),
        types: formData.types,
        from: formData.dateFrom,
        to: formData.dateTo,
      },
    };
  },

  getFromUrl() {
    const { CONFIG } = RevistaSearch;
    const usp = new URLSearchParams(window.location?.search || '');
    const get = (key) => usp.get(CONFIG.URL_PARAM[key])?.trim() || null;

    const params = {};

    const query = get('QUERY');
    if (query) params.query = query;

    const match = get('MATCH');
    if (['all', 'any', 'phrase'].includes(match)) params.match = match;

    const sort = get('SORT');
    if (['relevance', 'date-desc'].includes(sort))
      params.sort = sort;

    const from = get('FROM');
    if (from) params.from = from;

    const to = get('TO');
    if (to) params.to = to;

    const types = get('TYPES');
    if (types) params.types = types;

    const page = parseInt(get('PAGE'), 10);
    if (!isNaN(page) && page >= 1) params.page = page;

    const size = parseInt(get('SIZE'), 10);
    if (!isNaN(size) && size >= 1) params.size = size;

    return params;
  },

  updateUrl(params) {
    const { FormManager, Mappers, ALL_TYPES_VALUE, CONFIG } = RevistaSearch;
    if (!window.history || !window.location) return;

    const formData = FormManager.getFormData();
    const urlParams = new URLSearchParams();

    urlParams.set(CONFIG.URL_PARAM.QUERY, params.query || '');

    if (formData.match && formData.match !== 'all') {
      urlParams.set(CONFIG.URL_PARAM.MATCH, formData.match);
    }

    if (formData.sort && formData.sort !== 'relevance') {
      urlParams.set(CONFIG.URL_PARAM.SORT, formData.sort);
    }

    const currentPage = params.page + 1;
    const defaultPage = CONFIG.PAGINATION.DEFAULT_PAGE + 1;
    if (currentPage !== defaultPage) {
      urlParams.set(CONFIG.URL_PARAM.PAGE, String(currentPage));
    }

    const defaultSize = CONFIG.PAGINATION.DEFAULT_SIZE;
    if (params.size !== defaultSize) {
      urlParams.set(CONFIG.URL_PARAM.SIZE, String(params.size));
    }

    if (params.from) urlParams.set(CONFIG.URL_PARAM.FROM, params.from);
    if (params.to) urlParams.set(CONFIG.URL_PARAM.TO, params.to);

    if (params.types) {
      const isAllTypes =
        params.types === ALL_TYPES_VALUE || params.types === 'all';

      if (!isAllTypes) {
        const typesForUrl = params.types
          .split(',')
          .map((t) => Mappers.apiTypeToForm(t))
          .join(',');

        if (typesForUrl && typesForUrl !== 'all') {
          urlParams.set(CONFIG.URL_PARAM.TYPES, typesForUrl);
        }
      }
    }

    const newUrl = window.location.pathname + '?' + urlParams.toString();
    const fullUrl = window.location.origin + newUrl;

    if (window.location.href !== fullUrl) {
      window.history.replaceState(
        { searchParams: urlParams.toString() },
        '',
        newUrl
      );
    }
  },
};

// ============================================================================
// SERVIÇO DE API
// ============================================================================

RevistaSearch.APIService = {
  buildSearchUrl(params) {
    const { CONFIG } = RevistaSearch;
    const url = new URL(
      CONFIG.API.BASE_URL + CONFIG.API.SEARCH_ENDPOINT,
      window.location.origin
    );
    const sp = url.searchParams;

    sp.set('query', params.query || '');
    sp.set('page', String(Math.max(0, params.page)));
    sp.set('size', String(Math.max(1, params.size)));
    sp.set('order', params.order || CONFIG.SEARCH.ORDER.MOST_RELEVANT);
    sp.set('searchType', params.searchType || CONFIG.SEARCH.TYPE.ALL);

    if (params.types) sp.set('types', params.types);
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);

    return url.toString();
  },

  cancelPending(reason) {
    const { state, CONFIG } = RevistaSearch;
    reason = reason || CONFIG.CANCEL_REASON.NEW_SEARCH;
    if (state.abortController) {
      state.lastCancelReason = reason;
      state.abortController.abort();
      state.abortController = null;
    }
  },

  async fetchWithTimeout(url, timeoutMs) {
    const { state, CONFIG } = RevistaSearch;
    const controller = new AbortController();
    state.abortController = controller;
    state.lastCancelReason = null;

    const timeoutId = setTimeout(() => {
      state.lastCancelReason = CONFIG.CANCEL_REASON.TIMEOUT;
      controller.abort();
    }, timeoutMs);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = new Error(
          `Erro ao buscar resultados (HTTP ${response.status}).`
        );
        error.httpError = true;
        error.status = response.status;
        error.statusText = response.statusText;
        throw error;
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    } finally {
      state.abortController = null;
    }
  },

  normalizeError(error) {
    const { state, CONFIG } = RevistaSearch;

    if (error.name === 'AbortError') {
      if (state.lastCancelReason === CONFIG.CANCEL_REASON.TIMEOUT) {
        return {
          type: CONFIG.ERROR_TYPE.TIMEOUT,
          message: 'Tempo limite de resposta excedido.',
        };
      }
      return { type: CONFIG.ERROR_TYPE.CANCELLED, message: 'Busca cancelada.' };
    }

    if (error.httpError) {
      return {
        type: CONFIG.ERROR_TYPE.HTTP,
        message: error.message,
        status: error.status,
        statusText: error.statusText,
      };
    }

    if (error instanceof TypeError) {
      return {
        type: CONFIG.ERROR_TYPE.NETWORK,
        message: 'Falha de rede ao realizar a busca.',
        details: error.message,
      };
    }

    return {
      type: CONFIG.ERROR_TYPE.UNKNOWN,
      message: error.message || 'Ocorreu um erro inesperado.',
      details: error,
    };
  },

  _extractTotal(data, fallback) {
    if (typeof data?.total === 'number') return data.total;
    if (typeof data?.totalElements === 'number') return data.totalElements;
    return fallback || null;
  },

  async search(params) {
    const { CONFIG } = RevistaSearch;
    this.cancelPending();
    const url = this.buildSearchUrl(params);

    try {
      const data = await this.fetchWithTimeout(url, CONFIG.API.TIMEOUT_MS);
      const content = Array.isArray(data)
        ? data
        : data?.items || data?.content || [];

      return {
        success: true,
        data: {
          content,
          totalElements: this._extractTotal(data, content.length),
        },
      };
    } catch (error) {
      return { success: false, error: this.normalizeError(error) };
    }
  },
};

// ============================================================================
// CONTROLADOR PRINCIPAL
// ============================================================================

RevistaSearch.SearchController = {
  async performSearch(params) {
    const { UIState, APIService, ResultsRenderer, CONFIG, state } =
      RevistaSearch;

    state.currentParams = params;

    UIState.setLoading(true);
    UIState.setPaginationVisible(true);
    UIState.showSkeleton(params.size);
    UIState.updateResultsInfo(0, params.size, 0, 0, params.query);

    const result = await APIService.search(params);
    UIState.setLoading(false);

    if (result.success) {
      ResultsRenderer.render(result.data.content, {
        query: params.query,
        page: params.page,
        size: params.size,
        total: result.data.totalElements,
      });

      if (state.paginationController && result.data.totalElements != null) {
        const totalPages = Math.ceil(result.data.totalElements / params.size);
        state.paginationController.update(
          totalPages,
          params.page + 1,
          params.size
        );
      }
    } else if (result.error.type !== CONFIG.ERROR_TYPE.CANCELLED) {
      console.error('Erro na busca:', result.error);
      if (result.error.type === CONFIG.ERROR_TYPE.HTTP) {
        ResultsRenderer.renderErrorState(result.error);
      } else {
        UIState.hideSkeleton();
      }
    }
  },

  handleSubmit(event) {
    const { ParamsManager, UIState } = RevistaSearch;
    event.preventDefault();

    const result = ParamsManager.buildFromForm();

    if (result.error) {
      console.error('Erro de validação:', result.error.message);
      UIState.hideSkeleton();
      UIState.setLoading(false);
      UIState.resetResultsInfo();
      return;
    }

    ParamsManager.updateUrl(result.params);
    this.performSearch(result.params);
  },

  initializeFromUrl() {
    const { ParamsManager, FormManager, UIState, ResultsRenderer } =
      RevistaSearch;
    const urlParams = ParamsManager.getFromUrl();

    if (!urlParams || !urlParams.query) {
      UIState.hideSkeleton();
      UIState.setLoading(false);
      UIState.setPaginationVisible(false);
      UIState.resetResultsInfo();

      if (
        ResultsRenderer &&
        typeof ResultsRenderer.renderInitialState === 'function'
      ) {
        ResultsRenderer.renderInitialState();
      }

      return;
    }

    FormManager.setFormData(urlParams);
    const result = ParamsManager.buildFromForm();

    if (result.error) {
      console.error('Erro nos parâmetros da URL:', result.error.message);
      return;
    }

    if (urlParams.page) result.params.page = Math.max(0, urlParams.page - 1);
    if (urlParams.size) result.params.size = urlParams.size;

    ParamsManager.updateUrl(result.params);
    this.performSearch(result.params);
  },
};
